/**
 * Entity.ts — Base for anything that moves on the ground plane.
 *
 * Replaces `Phaser.Physics.Arcade.Sprite`. Gameplay state is 2D (`pos`/`vel` in
 * world x/y); `syncMesh()` lifts that into the Three.js scene as (x, 0, y) and
 * applies the current yaw. Movement integration and collision are explicit and
 * live in the systems that own them, so nothing is hidden behind an engine.
 */
import * as THREE from 'three';
import { vec2, type Vec2 } from '../utils/vec2';
import { facingYaw, turnToward } from './locomotion';
import { smoothing } from '../utils/math';
import { ANIM } from '../config/constants';

export class Entity {
  readonly pos: Vec2;
  readonly vel: Vec2 = vec2();
  readonly radius: number;
  readonly object: THREE.Object3D;

  /** Current facing (radians about world +y). */
  yaw = 0;
  /** Walk-cycle phase, advanced by distance travelled (see ANIM.PHASE_PER_UNIT). */
  walkPhase = 0;

  constructor(x: number, y: number, radius: number, object: THREE.Object3D) {
    this.pos = vec2(x, y);
    this.radius = radius;
    this.object = object;
    this.syncMesh();
  }

  /** Integrate velocity over `dt` seconds. Collision is applied by the caller. */
  integrate(dt: number): void {
    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;
  }

  /**
   * Advance the walk phase by the distance actually travelled this frame, and
   * turn smoothly toward the direction of travel.
   */
  advanceAnimation(dt: number): void {
    const speed = Math.hypot(this.vel.x, this.vel.y);
    this.walkPhase += speed * dt * ANIM.PHASE_PER_UNIT;
    this.yaw = turnToward(
      this.yaw,
      facingYaw(this.vel.x, this.vel.y, this.yaw),
      smoothing(ANIM.TURN_LERP, dt),
    );
  }

  /** Push gameplay state into the Three.js object. */
  syncMesh(): void {
    this.object.position.set(this.pos.x, 0, this.pos.y);
    this.object.rotation.y = this.yaw;
  }

  setPosition(x: number, y: number): void {
    this.pos.x = x;
    this.pos.y = y;
    this.syncMesh();
  }

  stop(): void {
    this.vel.x = 0;
    this.vel.y = 0;
  }
}
